import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { format } from "date-fns";
import { Sparkles, Eye, EyeOff, Heart, X, Gift } from "lucide-react";
import { useRegistry } from "@/context/RegistryContext";
import { ALL_ITEMS, PRIORITIES } from "@/data/marketplace";
import { MarketplaceCard } from "@/components/MarketplaceCard";
import { ContributeModal } from "@/components/ContributeModal";
import { BottomNav } from "@/components/BottomNav";
import { PublishButton } from "@/components/PublishButton";
import { DASHBOARD } from "@/constants/testIds";

export default function Dashboard() {
  const {
    profile,
    registry,
    removeItem,
    totalRaised,
    givers,
    view,
    setView,
  } = useRegistry();
  const [modalOpen, setModalOpen] = useState(false);

  if (!profile) return <Navigate to="/onboarding" replace />;

  return (
    <div data-testid={DASHBOARD.root} className="bg-[#FDFBF7] pb-28 md:pb-0">
      <section className="border-b border-[#E5E1D8] bg-[#F4F1EA]/50">
        <div className="mx-auto max-w-6xl px-6 md:px-10 py-10 md:py-14 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <div>
            <span className="text-xs tracking-[0.25em] uppercase text-[#5C6B62]">
              {view === "mother" ? "Her care registry" : "A public registry"}
            </span>
            <h1 className="font-display text-3xl md:text-5xl text-[#2D3732] mt-3 leading-tight">
              {view === "mother" ? (
                <>Curated for<br /><span className="italic text-[#D98A71]">your recovery.</span></>
              ) : (
                <>A gentle way<br /><span className="italic text-[#D98A71]">to show up.</span></>
              )}
            </h1>
            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-[#5C6B62]">
              {profile.dueDate && <span>Due {format(profile.dueDate, "d MMM yyyy")}</span>}
              <span className="inline-flex items-center gap-2">
                <Sparkles size={14} strokeWidth={1.5} />
                {profile.priorities
                  .map((p) => PRIORITIES.find((x) => x.id === p)?.label)
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </div>
          </div>

          <button
            data-testid={DASHBOARD.viewToggle}
            onClick={() => setView(view === "mother" ? "guest" : "mother")}
            className="self-start md:self-auto rounded-full bg-[#2D3732] text-[#FDFBF7] px-5 py-3 text-sm font-medium inline-flex items-center gap-2 hover:bg-[#1e2521] transition-colors"
          >
            {view === "mother" ? (
              <><Eye size={16} strokeWidth={1.5} />Preview guest view</>
            ) : (
              <><EyeOff size={16} strokeWidth={1.5} />Back to your view</>
            )}
          </button>
        </div>
      </section>

      {view === "mother" ? (
        <MotherView
          items={ALL_ITEMS}
          registry={registry}
          removeItem={removeItem}
          totalRaised={totalRaised}
          givers={givers}
        />
      ) : (
        <GuestView
          registry={registry}
          totalRaised={totalRaised}
          givers={givers}
          onContribute={() => setModalOpen(true)}
        />
      )}

      <ContributeModal open={modalOpen} onOpenChange={setModalOpen} />
      <BottomNav />
    </div>
  );
}

const CareFundHeader = ({ totalRaised, showCTA, onContribute }) => (
  <div
    data-testid="registry-aggregate-total"
    className="mt-8 rounded-3xl bg-[#FDFBF7] border border-[#E5E1D8] p-6 md:p-8"
  >
    <div className="flex flex-wrap items-baseline justify-between gap-4">
      <div>
        <p className="text-[10px] tracking-[0.25em] uppercase text-[#5C6B62]">
          Care fund · Total raised
        </p>
        <p className="font-display text-4xl md:text-5xl text-[#2D3732] mt-2">
          <span data-testid="total-raised">${totalRaised}</span>
          <span className="text-base text-[#5C6B62] ml-2">NZD</span>
        </p>
        <p className="text-sm text-[#5C6B62] mt-2 max-w-md">
          Contributions of any size go toward the wishlist she's chosen — meals, help at home, rest, and small joys.
        </p>
      </div>
      {showCTA && (
        <button
          data-testid="care-fund-contribute-btn"
          onClick={onContribute}
          className="rounded-full bg-[#D98A71] hover:bg-[#c67a63] text-[#FDFBF7] px-6 py-3 text-sm font-medium inline-flex items-center gap-2 transition-colors"
        >
          <Heart size={16} strokeWidth={1.5} />
          Contribute to Care Fund
        </button>
      )}
    </div>
  </div>
);

const GiftedBy = ({ givers }) => {
  if (givers.length === 0) return null;
  return (
    <div data-testid="gifted-by-section" className="mt-6 rounded-3xl bg-[#F4F1EA]/60 border border-[#E5E1D8] p-6 md:p-8">
      <div className="flex items-center gap-3">
        <Gift size={16} strokeWidth={1.5} className="text-[#D98A71]" />
        <p className="text-[10px] tracking-[0.25em] uppercase text-[#5C6B62]">Gifted by</p>
      </div>
      <ul className="mt-5 divide-y divide-[#E5E1D8]">
        {givers.map((g, i) => (
          <li key={i} data-testid={`giver-row-${i}`} className="py-3 flex items-center justify-between gap-4 text-sm">
            <div className="min-w-0">
              <p className="font-medium text-[#2D3732] truncate">{g.giver}</p>
              {g.message && (
                <p className="text-xs text-[#5C6B62] italic truncate">"{g.message}"</p>
              )}
            </div>
            <span className="font-display text-lg text-[#2D3732] whitespace-nowrap">${g.amount}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const MotherView = ({ items, registry, removeItem, totalRaised, givers }) => (
  <div data-testid={DASHBOARD.motherView}>
    <section data-testid={DASHBOARD.motherRegistry} className="mx-auto max-w-6xl px-6 md:px-10 pt-14 md:pt-20">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <span className="text-xs tracking-[0.25em] uppercase text-[#5C6B62]">Her wishlist</span>
          <h2 className="font-display text-3xl md:text-4xl text-[#2D3732] mt-3">
            {registry.length === 0
              ? "Nothing yet — add what she'd love."
              : `${registry.length} ${registry.length === 1 ? "thing" : "things"} on her wishlist.`}
          </h2>
        </div>
        <PublishButton />
      </div>

      <CareFundHeader totalRaised={totalRaised} />

      {registry.length === 0 ? (
        <div className="mt-6 rounded-3xl border border-dashed border-[#E5E1D8] bg-[#F4F1EA]/40 p-10 text-center">
          <p className="text-[#5C6B62]">
            Browse below and tap <span className="text-[#2D3732]">Add to My Wishlist</span> on the things she'd love most.
          </p>
        </div>
      ) : (
        <>
          <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {registry.map((entry) => (
              <div
                key={entry.item.id}
                data-testid={DASHBOARD.registryItem(entry.item.id)}
                className="bg-[#FDFBF7] border border-[#E5E1D8] rounded-2xl p-5 flex gap-4 items-start"
              >
                <img src={entry.item.image} alt="" className="w-16 h-16 rounded-xl object-cover flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] text-[#5C6B62] truncate">{entry.item.tag}</p>
                  <p className="font-display text-base text-[#2D3732] mt-1 leading-tight truncate">
                    {entry.item.title}
                  </p>
                </div>
                <button
                  aria-label="Remove"
                  onClick={() => removeItem(entry.item.id)}
                  className="text-[#5C6B62] hover:text-[#D98A71] transition-colors p-1"
                >
                  <X size={16} strokeWidth={1.5} />
                </button>
              </div>
            ))}
          </div>
          <GiftedBy givers={givers} />
        </>
      )}
    </section>

    <section data-testid={DASHBOARD.nationwide} className="mx-auto max-w-6xl px-6 md:px-10 pt-20 md:pt-28 pb-24">
      <div>
        <span className="text-xs tracking-[0.25em] uppercase text-[#5C6B62]">Care marketplace</span>
        <h2 className="font-display text-3xl md:text-4xl text-[#2D3732] mt-3 leading-tight">
          Everything she<br />actually needs.
        </h2>
        <p className="mt-4 text-sm text-[#5C6B62] max-w-xl">
          Meals, in-home help, recovery essentials, and small joys — available across Aotearoa. Delivery details are captured at contribution.
        </p>
      </div>
      <div className="mt-10 grid gap-6 md:gap-8 md:grid-cols-2 lg:grid-cols-3">
        {items.map((item) => (
          <MarketplaceCard key={item.id} item={item} />
        ))}
      </div>
    </section>
  </div>
);

const GuestView = ({ registry, totalRaised, givers, onContribute }) => (
  <section data-testid={DASHBOARD.guestView} className="mx-auto max-w-4xl px-6 md:px-10 py-14 md:py-20">
    {registry.length === 0 ? (
      <div className="rounded-3xl border border-dashed border-[#E5E1D8] bg-[#F4F1EA]/40 p-14 text-center">
        <p className="font-display text-2xl text-[#2D3732]">Her wishlist is still being curated.</p>
        <p className="mt-3 text-[#5C6B62]">Switch back to your view and add items to see how they'll appear here.</p>
      </div>
    ) : (
      <>
        <div className="rounded-3xl bg-[#F4F1EA] border border-[#E5E1D8] p-8 md:p-10">
          <p className="text-xs tracking-[0.25em] uppercase text-[#5C6B62]">A note from Held</p>
          <p className="font-display text-2xl md:text-3xl text-[#2D3732] mt-4 leading-snug">
            "She'd love your help with any of these. Give any amount to her Care Fund — every dollar becomes a real, delivered gift of care."
          </p>
          <CareFundHeader
            totalRaised={totalRaised}
            showCTA
            onContribute={onContribute}
          />
        </div>

        <div className="mt-10">
          <p className="text-xs tracking-[0.25em] uppercase text-[#5C6B62]">
            On her wishlist
          </p>
          <div className="mt-6 grid gap-5 md:grid-cols-2">
            {registry.map((entry) => (
              <article
                key={entry.item.id}
                data-testid={DASHBOARD.registryItem(entry.item.id)}
                className="bg-[#FDFBF7] border border-[#E5E1D8] rounded-3xl overflow-hidden flex"
              >
                <img
                  src={entry.item.image}
                  alt=""
                  className="w-32 h-full object-cover flex-shrink-0"
                />
                <div className="p-5 flex-1 min-w-0">
                  <p className="text-[11px] text-[#5C6B62]">{entry.item.tag}</p>
                  <h3 className="font-display text-lg text-[#2D3732] mt-1 leading-tight">
                    {entry.item.title}
                  </h3>
                  <p className="text-xs text-[#5C6B62] mt-2 line-clamp-3">
                    {entry.item.description}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>

        <GiftedBy givers={givers} />
      </>
    )}
  </section>
);
